import { setLastPurchase, getLastPurchase } from '../redux/utils/cookies';

const getVariant = (variant) => (variant ? variant.map((vari) => vari.value).join(', ') : null);

/**
 * Envia o evento ao Google Analytics
 * ---> https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce.
 *
 * @param {String} eventAction Especifica a ação do evento. Não pode estar vazio.
 * @param {String} eventLabel Especifica o rótulo do evento.
 * @param {String} eventValue Especifica o valor do evento. Os valores não podem ser negativos.
 * @param {Function} hitCallback Funçaõ de callback chamada do retorno no evento.
 *
 * @returns Função sem retorno, envia o evento com tipo e as informações ao analytics e ads.
 */
export function gaEventEmitter(eventAction, eventLabel, eventValue, hitCallback) {
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && window.ga) {
    window.ga('set', 'currencyCode', 'BRL');

    return window.ga('send', 'event', {
      eventCategory: 'Ecommerce',
      eventAction,
      eventLabel,
      eventValue,
      hitCallback,
    });
  }

  return process.env.NODE_ENV !== 'production'
    ? console.log("Don't tracking google analytics in development or GA global function was not found")
    : null;
}

/**
 * Envia um evento ec:setAction com a ação e o dados da ação
 * ---> https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce?hl=pt-br#action-types.
 *
 * @param {Object} action string tipo da ação.
 * @param {Object} actionOptions objeto com as opções da ação conforme documentação.
 */
export function gaSetAction(action, actionOptions = {}) {
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && window.ga) {
    window.ga('ec:setAction', action, actionOptions);
  }

  return process.env.NODE_ENV !== 'production'
    ? console.log("Don't tracking 'SET ACTION' google analytics in development or GA global function was not found")
    : null;
}

/**
 * Envia um evento ec:addProduct com objeto de produto
 * ---> https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce?hl=pt-br#product-data.
 *
 * @param {Object} item objeto produto do array "lines" do carrinho.
 */
export function gaAddProduct(item, position) {
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && window.ga) {
    window.ga('ec:addProduct', {
      id: item.product?.sku,
      name: item.product?.name,
      brand: item.product?.supplier,
      category: null, // categoria do produto
      variant: getVariant(item.product.diffs),
      price: item.product.price,
      quantity: item.quantity,
      coupon: null, // cupom do produto
      position,
    });
  }

  return process.env.NODE_ENV !== 'production'
    ? console.log("Don't tracking 'ADD PRODUCT' google analytics in development or GA global function was not found")
    : null;
}

/**
 * Envia um evento ao analytics ao adicionar itens ao carrinho.
 *
 * @param {Array} Lines array "lines" do carrinho.
 */
export function gaAddToCart(lines = [], remove) {
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && window.ga && lines.length > 0) {
    const type = {
      value: remove ? 'remove' : 'add',
      label: remove ? 'remove from cart' : 'add to cart',
    };

    for (let index = 0; index < lines.length; index++) {
      gaAddProduct(lines[index], index + 1);
      gaSetAction(type.value);
    }

    return gaEventEmitter(type.label);
  }

  return process.env.NODE_ENV !== 'production'
    ? console.log("Don't tracking 'ADD TO CART' google analytics in development or GA global function was not found")
    : null;
}

/**
 * Envia um evento ao analytics ao remover itens ao carrinho.
 *
 * @param {Array} lines array "lines" do carrinho.
 */
export function gaRemoveFromCart(lines = []) {
  return gaAddToCart(lines, true);
}

/**
 * Envia um evento de início do processo de checkout para um ou mais produtos.
 *
 * @param {Array} lines array "lines" do carrinho
 */
export function gaBeginCheckout(lines = []) {
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && window.ga) {
    if (lines.length > 0) {
      for (let index = 0; index < lines.length; index++) {
        gaAddProduct(lines[index], index + 1);
      }
    }

    gaSetAction('checkout');

    return gaEventEmitter('Begin Checkout');
  }

  return process.env.NODE_ENV !== 'production'
    ? console.log("Don't tracking 'BEGIN CHECKOUT' google analytics in development or GA global function was not found")
    : null;
}

/**
 * Envia um evento 'checkout_option' com o step do processo de finalização de compra.
 *
 * @param {Number} step O número da etapa concluída do checkout.
 * @param {String} option A opção selecionada.
 */
export function gaCheckoutStep(step, option) {
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && window.ga) {
    gaSetAction('checkout', {
      step,
      option,
    });

    return gaEventEmitter('Checkout Step');
  }

  return process.env.NODE_ENV !== 'production'
    ? console.log("Don't tracking 'CHECKOUT STEP' google analytics in development or GA global function was not found")
    : null;
}

/**
 * Envia o evento de venda de um ou mais produtos.
 *
 * @param {Object} order Objeto com as informações do pedido.
 * @param {Array} lines Array com os itens, lines do carrinho.
 */
export function gaPurchase(order = {}, lines = [], seller, domain) {
  const lastOrderId = getLastPurchase(domain);
  const currentOrderId = order.getIn(['pedido']) ? order.getIn(['pedido']).toString() : '';

  if (
    process.env.NODE_ENV === 'production' &&
    typeof window !== 'undefined' &&
    window.ga &&
    (!lastOrderId || lastOrderId !== currentOrderId)
  ) {
    if (lines.length > 0) {
      for (let index = 0; index < lines.length; index++) {
        gaAddProduct(lines[index], index + 1);
      }
    }

    gaSetAction('purchase', {
      id: order.getIn(['pedido']),
      affiliation: `${seller} - Ecommerce`,
      revenue: order.valorLiquido,
      tax: null, // Impostos
      shipping: order.valorFrete,
      coupon: order.coupon, // Cupom de desconto
      // currency: 'BRL',
    });

    const purchaseCallback = () => {
      setLastPurchase(order.getIn(['pedido']), domain);
      gaCheckoutStep(5, 'Confirmação');
    };

    return gaEventEmitter('Purchase', null, null, purchaseCallback);
  }

  return process.env.NODE_ENV !== 'production'
    ? console.log(
        // eslint-disable-next-line max-len
        "Don't tracking 'PURCHASE' google analytics in development, GA global function was not found or order id already exists"
      )
    : null;
}

/**
 * Envia as informações do pedido ao Ads.
 *
 * @param {Object} order Objeto com as informações do pedido.
 *
 */
export const adsConversion = (order = {}) => {
  const sellers = order.getIn(['sellers']);

  const lastOrderId = getLastPurchase();
  const currentOrderId = sellers.length === 1 ? sellers.getIn([0, 'id']) : order.getIn(['carrinho']);

  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && lastOrderId !== currentOrderId) {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    window.dataLayer.push({
      currency: 'BRL',
      transaction_value: order.getIn(['valorTotal']),
      transaction_id: currentOrderId,
    });
  }

  return process.env.NODE_ENV !== 'production'
    ? console.log(
        "Don't tracking 'CONVERSION' google Ads in development, DATA LAYER was not found or order id already exists"
      )
    : null;
};

/**
 * Envia as informações de remarketing ao Ads.
 *
 * @param {string} event evento https://support.google.com/google-ads/answer/7305793 .
 * @param {number} value valor dos items.
 * @param {Array} items array de objetos com "id" do produto.
 */
export const adsRemarketing = (event, value, items) => {
  if (typeof window !== 'undefined' && items && items.length) {
    const others = window.dataLayer || [];

    window.dataLayer = [...others];

    window.dataLayer.push({
      event,
      value,
      items: items.map((item) => ({ id: item.id, google_business_vertical: 'retail' })),
    });
  }
};

export default { gaCheckoutStep, gaAddToCart, gaRemoveFromCart, gaPurchase, adsConversion, gaBeginCheckout };
